import { useFormik } from 'formik';
import { FC, useContext, useState } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Input, Password, Text, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import fullLogo from '../../assets/logo_completo.png';
import { PrivilegeContext } from '../../components/priviledge/PriviledgeProvider';
import { User, login } from '../../redux/authSlice';
import { AppDispatch, RootState } from '../../redux/store';
import { LoginService } from '../../services/auth/loginService';
import cn from '../../utils/classNames';
import { getAuthenticatedHomePath } from '../../utils/dashboardAccess';

const Login: FC = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { userCan, isFacilitator, isFacilitatorManager } = useContext(PrivilegeContext);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [isLoading, setIsLoading] = useState(false);
  const [remindMe, setRemindMe] = useState(false);

  if (isAuthenticated) {
    setTimeout(() => {
      navigate(getAuthenticatedHomePath(userCan, { isFacilitator, isFacilitatorManager }), { replace: true });
    }, 100);
  };

  const handleLogin = async (username: string, password: string) => {
    setIsLoading(true);
    const response = await (await (new LoginService()).authUserCredentials(username, password, remindMe)).getResponseData();

    if (!response?.success) {
      toast.error(response.message);
      setIsLoading(false);
      dispatch(login({
        isAuthenticated: false,
        loading: false,
        error: response.message,
        user: null
      }))
      return;
    }

    if (response && response.success) {
      // store data in indexDb for service worker.
      try {
        //saveUser(response.data);
        const user: User = {
          id: response.data.user.id,
          email: response.data.user.email,
          token: response.data.token,
          name: response.data.user.name,
          lastName: response.data.user.lastName,
          profileImage: response.data.user.imgProfile,
          roles: response.data.user.roles,
          commercialNetwork: response.data.user.commercialNetworks || '',
          department: response.data.user.department || '',
          departmentHandlesCommunications: Boolean(response.data.user.departmentHandlesCommunications),
          companyName: response.data.user.companyName || '',
          ocmUserLogin: response.data.user.ocmUserLogin
        };

        // dispatch login action
        dispatch(login({
          isAuthenticated: true,
          loading: false,
          error: null,
          user: user
        }))
      } catch (error) {
        toast.error('Error saving user to indexDb');
        return;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      loginUsername: '',
      loginPassword: '',
    },
    validate: (values) => {
      const errors: { loginUsername?: string; loginPassword?: string } = {};

      if (!values.loginUsername) {
        errors.loginUsername = 'Requerido';
      }

      if (!values.loginPassword) {
        errors.loginPassword = 'Requerido';
      }

      return errors;
    },
    validateOnChange: false,
    onSubmit: (values) => { handleLogin(values.loginUsername, values.loginPassword) },
  });

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-sky-100 via-white to-indigo-100">
      {/* Decorative blurred background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="blob blob-1 absolute -top-32 -left-32 h-96 w-96 rounded-full bg-sky-300/40 blur-3xl" />
        <div className="blob blob-2 absolute top-1/3 -right-32 h-96 w-96 rounded-full bg-indigo-300/40 blur-3xl" />
      </div>
      {/* 
      <div className="float-start">
        <Link to='/' className={'flex justify-center items-center m-5'}>
          <img src={fullLogo} width={'150px'} alt="Logo de Attiva" />
        </Link>
      </div>
      */}

      <div className=' w-full flex-col justify-center p-4 md:p-12 lg:p-28'>
        <div
          className={cn(
            'relative mx-auto w-full max-w-md rounded-2xl px-6 py-10 flex flex-col justify-center',
          )}
        >
          <div className="pointer-events-none absolute inset-0 rounded-2xl" />
          <div className='flex flex-col justify-center text-center mb-5'>
            <Link to='/' className={'flex justify-center items-center mb-5'}>
              <img
                src={fullLogo}
                alt="Logo de Attiva"
                className="h-14 w-auto"
              />
            </Link>

            <Text className="text-2xl font-semibold text-gray-900">
              Accede a tu cuenta
            </Text>
            <Text className="mt-1 text-sm text-gray-600">
              Gestiona contratos, clientes y seguimiento comercial desde un solo lugar.
            </Text>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="space-y-5">
              <Input
                id='loginUsername'
                type="email"
                size="lg"
                label="Email"
                autoComplete="email"
                placeholder="Introduce tu email"
                className="[&>label>span]:font-medium"
                inputClassName="text-sm"
                onChange={formik.handleChange}
                error={formik.errors.loginUsername}
              />
              <Password
                id='loginPassword'
                label="Contraseña"
                placeholder="Introduce tu contraseña"
                autoComplete="current-password"
                size="lg"
                className="[&>label>span]:font-medium"
                inputClassName="text-sm"
                value={formik.values.loginPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.loginPassword}
              />

              <Checkbox
                id='remindMe'
                label={
                  <Tooltip
                    size="md"
                    content={
                      <div className="tooltip-container">
                        Mantiene tu sesión activa durante 24 horas
                      </div>
                    }
                    placement="top"
                    color="invert"
                  >
                    <div className={'flex ms-1'}>
                      Recuérdame <HiOutlineInformationCircle size={20} color={'primary'} className='ms-2' />
                    </div>
                  </Tooltip>
                }
                checked={remindMe}
                onChange={() => setRemindMe(!remindMe)}
                className="text-sm"
              />

              <Button
                className="w-full"
                type="submit"
                size="lg"
                disabled={isLoading}
                isLoading={isLoading}
              >
                {isLoading ? 'Iniciando sesión…' : 'Iniciar sesión'}
              </Button>
            </div>
          </form>
          <Text className="mt-5 text-center text-[15px] leading-loose text-gray-500 md:mt-7 lg:mt-9 lg:text-base">
            ¿No recuerdas la contraseña?{' '}
            <Link
              to="/forgot-password"
              className="font-semibold text-gray-700 transition-colors hover:text-gray-1000"
            >
              Recuperar contraseña
            </Link>
          </Text>

          {/* Acceder al login de tutor */}
          <div className='flex flex-col justify-center items-center h-full'>
            <Text className="text-sm text-gray-700 mt-5">
            ¿Eres tutor?{' '}
            <Link to="/tutor-login" className="font-semibold">
              Accede a tu panel de gestión
            </Link>
          </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
